import { isString } from '@/utils/is'
import { toNumber } from '@/utils/number'
import { camelToKebab } from '@/utils/formatting/case'
import twColors from 'tailwindcss/colors'
import { theme as findexTheme } from 'findef'

import type { CSSProperties } from 'vue'
import type { TStyleVariant } from '@/types/style/variant'

// Convert OKLCH (used by Tailwind v4's color palette) to hex so Vector.fromColor() can parse it.
function oklchToHex(str: string): string {
  const match = str.match(/oklch\(\s*([\d.]+)(%?)\s+([\d.]+)\s+([\d.]+)/)
  if (!match) return str

  let L = parseFloat(match[1])
  const C = parseFloat(match[3])
  const H = parseFloat(match[4])
  if (match[2] === '%') L = L / 100

  const h = H * Math.PI / 180
  const a = C * Math.cos(h)
  const b = C * Math.sin(h)

  const l_ = L + 0.3963377774 * a + 0.2158037573 * b
  const m_ = L - 0.1055613458 * a - 0.0638541728 * b
  const s_ = L - 0.0894841775 * a - 1.2914855480 * b

  const l = l_ * l_ * l_
  const m = m_ * m_ * m_
  const s = s_ * s_ * s_

  let r = +4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s
  let g = -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s
  let bl = -0.0041960863 * l - 0.7034186147 * m + 1.7076147010 * s

  const toSRGB = (c: number): number => {
    const abs = Math.abs(c)
    if (abs <= 0.0031308) return 12.92 * c
    return (c < 0 ? -1 : 1) * (1.055 * Math.pow(abs, 1 / 2.4) - 0.055)
  }
  r = toSRGB(r)
  g = toSRGB(g)
  bl = toSRGB(bl)

  const byte = (c: number): string =>
    Math.round(Math.min(1, Math.max(0, c)) * 255).toString(16).padStart(2, '0')
  return `#${byte(r)}${byte(g)}${byte(bl)}`
}

// Static map of all available colors: "teal-400" -> "#2dd4bf", "primary-600" -> "#..."
// Built at module load time from Tailwind's palette + findef custom colors.
// OKLCH values from tailwindcss/colors are converted to hex so Vector.fromColor() works.
const colorMap: Record<string, string> = {}

for (const [name, shades] of Object.entries(twColors)) {
  if (typeof shades === 'object' && shades !== null) {
    for (const [shade, value] of Object.entries(shades as Record<string, string>)) {
      const v = typeof value === 'string' && value.startsWith('oklch') ? oklchToHex(value) : value
      colorMap[`${name}-${shade}`] = v
    }
  } else if (typeof shades === 'string') {
    colorMap[name] = shades // inherit, current, transparent, black, white
  }
}

for (const [name, shades] of Object.entries(findexTheme.colors)) {
  for (const [shade, value] of Object.entries(shades as Record<string, string>)) {
    colorMap[`${name}-${shade}`] = value
  }
}

// Kept for cases that specifically need to read a CSS custom property at runtime.
const lookupCSSColor = (key: string): string => {
  if (typeof document === 'undefined') return '';
  return getComputedStyle(document.documentElement).getPropertyValue(`--color-${key}`).trim();
}

export const getAppTheme = (): Record<string, string> => {
  return new Proxy({} as Record<string, string>, {
    get: (_, key: string) => lookupCSSColor(key),
  });
}

export const twColor = (classname: string): string => {
  const getKey = (cn: string) => {
    if (cn.startsWith('bg-') || cn.startsWith('text-')) {
      return cn.slice(cn.indexOf('-') + 1)
    }
    return cn
  }

  const classes = classname.includes(' ') ? classname.split(' ') : [classname]
  for (const cls of classes) {
    const value = colorMap[getKey(cls)]
    if (value) return value
  }

  return ''
}

export const pxToRem = (px: number | string, baseFontSize: number = 16): number => {
  let n: number = 0

  if (isString(px)) {
    n = toNumber(px)
  } else {
    n = px
  }

  return n / baseFontSize
}

export const pxToRemStr = (px: number | string, baseFontSize: number = 16): string =>
  `${pxToRem(px, baseFontSize).toFixed(2)}rem`

const formatValue = (key: string, v: CSSProperties[keyof CSSProperties]): string => {
  return key === 'content' ? `"${v}"` : `${v}`
}

export const cssPropsToString = (style: CSSProperties): string => {
  return Object.entries(style)
    .map(([k, v]) => `${camelToKebab(k)}: ${formatValue(k, v)};`)
    .join('\n')
    .trimEnd()
}

export const classPairForVariant = (
  variant: TStyleVariant | undefined = undefined,
  shade: number = 0
): { bg: string; fg: string } => {
  const pair = (): [string, string] => {
    switch (variant) {
      case `white`:
        return [`bg-white`, `text-gray-${shade || 900}`]
      case `gray`:
        return [`border-gray-${shade || 300}`, `text-gray-${shade || 900}`]
      case `warning`:
        return [`bg-amber-${shade || 300}`, `text-gray-${shade || 500}`]
      case `transparent`:
        return [`bg-transparent`, `text-gray-${shade || 900}`]
      default:
      case `primary`:
        return [`bg-primary-${shade || 500}`, `text-white`]
    }
  }

  const [bg, fg] = pair()
  return { bg, fg }
}

export const blurClass = 'blur-xs select-none pointer-events-none'
